/*
 * Marketing/analytics tracking — Shopify port of src/lib/tracking.ts,
 * src/hooks/useOmnisend.ts and src/hooks/useShopifyAnalytics.ts from the
 * Lovable source. Base pixels (Stape GTM loader, Google Ads gtag, Omnisend
 * snippet, TikTok pixel) are loaded in layout/theme.liquid; this file only
 * fires the *events* (view_item, add_to_cart, begin_checkout, search,
 * generate_lead) and captures ad-click attribution so it survives into the
 * order via cart attributes.
 *
 * Exposes window.TBTrack. No other file should call ttq / dataLayer / gtag
 * directly — call TBTrack.* instead, same rule as the Lovable codebase.
 */
(function (window, document) {
  'use strict';

  var CURRENCY = 'USD';
  var ATTR_KEY = 'tb_attr';
  var ATTR_MAX_AGE = 60 * 60 * 24 * 90; // 90 days
  var ATTR_KEYS = ['fbclid', 'ttclid', 'gclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  function safe(fn) {
    try { fn(); } catch (e) { /* tracking must never break the UI */ }
  }

  function newEventId() {
    try {
      if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    } catch (e) { /* noop */ }
    return 'ev-' + Date.now() + '-' + Math.random().toString(16).slice(2);
  }

  function debugEnabled() {
    try {
      var params = new URLSearchParams(window.location.search);
      if (params.get('debug_tracking') === '1') {
        window.sessionStorage.setItem('tb_debug_tracking', '1');
        return true;
      }
      return window.sessionStorage.getItem('tb_debug_tracking') === '1';
    } catch (e) { return false; }
  }

  function debugLog(name, params, eventId) {
    if (!debugEnabled()) return;
    // eslint-disable-next-line no-console
    console.log('[tracking] ' + name, { event_id: eventId, params: params });
  }

  function ttqTrack(name, params, eventId) {
    safe(function () {
      if (window.ttq && typeof window.ttq.track === 'function') {
        window.ttq.track(name, params, { event_id: eventId });
      }
    });
  }

  function dlPush(payload) {
    safe(function () {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    });
  }

  function gtagEvent(name, params) {
    safe(function () {
      if (typeof window.gtag === 'function') window.gtag('event', name, params);
    });
  }

  function round2(n) {
    return Math.round((n || 0) * 100) / 100;
  }

  // items: [{id, name, price (dollars), quantity, category}]
  function normalize(items) {
    var clean = (items || []).filter(function (i) { return i && i.id; });
    var contents = clean.map(function (i) {
      return { id: String(i.id), quantity: i.quantity || 1, item_price: round2(i.price) };
    });
    var ttContents = clean.map(function (i) {
      return { content_id: String(i.id), content_type: 'product', content_name: i.name, quantity: i.quantity || 1, price: round2(i.price) };
    });
    var ga4Items = clean.map(function (i) {
      return { item_id: String(i.id), item_name: i.name, item_category: i.category, price: round2(i.price), quantity: i.quantity || 1 };
    });
    var value = round2(clean.reduce(function (s, i) { return s + (i.price || 0) * (i.quantity || 1); }, 0));
    var numItems = clean.reduce(function (s, i) { return s + (i.quantity || 1); }, 0);
    return { ids: clean.map(function (i) { return String(i.id); }), contents: contents, ttContents: ttContents, ga4Items: ga4Items, value: value, numItems: numItems };
  }

  // ---------------------------------------------------------------------
  // Attribution
  // ---------------------------------------------------------------------

  function getCookie(name) {
    try {
      var match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : '';
    } catch (e) { return ''; }
  }

  function setCookie(name, value, maxAge) {
    safe(function () {
      document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; max-age=' + (maxAge || ATTR_MAX_AGE) + '; SameSite=Lax';
    });
  }

  function getAttribution() {
    var stored = {};
    try {
      var raw = window.localStorage.getItem(ATTR_KEY) || getCookie(ATTR_KEY);
      if (raw) stored = JSON.parse(raw);
    } catch (e) { stored = {}; }
    return stored || {};
  }

  function persistAttribution(attr) {
    safe(function () {
      var json = JSON.stringify(attr);
      window.localStorage.setItem(ATTR_KEY, json);
      setCookie(ATTR_KEY, json);
    });
  }

  /** Capture click ids + utm params on this page load and sync to the Shopify cart as attributes. */
  function captureAttribution() {
    var current = getAttribution();
    var next = {};
    for (var k in current) next[k] = current[k];
    var changed = false;
    safe(function () {
      var params = new URLSearchParams(window.location.search);
      ATTR_KEYS.forEach(function (k) {
        var v = params.get(k);
        if (v && v.trim()) { next[k] = v.trim(); changed = true; }
      });
      if (!next.landing_page) { next.landing_page = window.location.href; changed = true; }

      var fbclid = params.get('fbclid') || next.fbclid;
      if (fbclid && !getCookie('_fbc')) {
        setCookie('_fbc', 'fb.1.' + Date.now() + '.' + fbclid);
      }
    });
    persistAttribution(next);
    if (changed) syncAttributionToCart(next);
    return next;
  }

  /** Push attribution onto the Shopify cart as note attributes, so it survives to the order. */
  function syncAttributionToCart(attr) {
    safe(function () {
      var attributes = {};
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'ttclid', 'gclid', 'landing_page'].forEach(function (k) {
        if (attr[k]) attributes[k] = attr[k];
      });
      var fbp = getCookie('_fbp');
      var fbc = getCookie('_fbc');
      if (fbp) attributes._fbp = fbp;
      if (fbc) attributes._fbc = fbc;
      if (Object.keys(attributes).length === 0) return;
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      }).catch(function () {});
    });
  }

  // ---------------------------------------------------------------------
  // Events
  // ---------------------------------------------------------------------

  function trackViewItem(product) {
    var eventId = newEventId();
    var n = normalize([product]);
    ttqTrack('ViewContent', { contents: n.ttContents, value: n.value, currency: CURRENCY }, eventId);
    dlPush({ event: 'view_item', event_id: eventId, ecommerce: { currency: CURRENCY, value: n.value, items: n.ga4Items } });
    gtagEvent('view_item', { value: n.value, currency: CURRENCY, items: n.ga4Items });
    debugLog('ViewContent', product, eventId);
    return eventId;
  }

  var recentAddToCart = {};
  function trackAddToCart(items) {
    var list = Object.prototype.toString.call(items) === '[object Array]' ? items : [items];
    var key = list.map(function (i) { return i.id + ':' + (i.quantity || 1); }).join(',');
    var now = Date.now();
    if (recentAddToCart[key] && now - recentAddToCart[key] < 800) return '';
    recentAddToCart[key] = now;
    var eventId = newEventId();
    var n = normalize(list);
    ttqTrack('AddToCart', { contents: n.ttContents, value: n.value, currency: CURRENCY }, eventId);
    dlPush({ event: 'add_to_cart', event_id: eventId, ecommerce: { currency: CURRENCY, value: n.value, items: n.ga4Items } });
    gtagEvent('add_to_cart', { value: n.value, currency: CURRENCY, items: n.ga4Items });
    debugLog('AddToCart', list, eventId);
    return eventId;
  }

  /** Call with the raw JSON response of POST /cart/add.js (has an `items` array with price in cents). */
  function trackAddToCartFromAddResponse(res) {
    if (!res) return '';
    var rawItems = res.items ? res.items : [res];
    var items = rawItems.map(function (it) {
      return { id: it.variant_id || it.id, name: it.product_title || it.title, price: (it.price || 0) / 100, quantity: it.quantity || 1 };
    });
    return trackAddToCart(items);
  }

  function trackBeginCheckout(items, overrideValue) {
    var eventId = newEventId();
    var n = normalize(items);
    var value = typeof overrideValue === 'number' ? round2(overrideValue) : n.value;
    ttqTrack('InitiateCheckout', { contents: n.ttContents, value: value, currency: CURRENCY }, eventId);
    dlPush({ event: 'begin_checkout', event_id: eventId, ecommerce: { currency: CURRENCY, value: value, items: n.ga4Items } });
    gtagEvent('begin_checkout', { value: value, currency: CURRENCY, items: n.ga4Items });
    debugLog('InitiateCheckout', items, eventId);
    return eventId;
  }

  /** Call with the Shopify `cart` object (cart.items) to fire begin_checkout from Liquid-side data. */
  function trackBeginCheckoutFromShopifyCart(cart) {
    if (!cart || !cart.items) return '';
    var items = cart.items.map(function (it) {
      return { id: it.variant_id || it.id, name: it.product_title || it.title, price: (it.price || 0) / 100, quantity: it.quantity || 1 };
    });
    return trackBeginCheckout(items, (cart.total_price || 0) / 100);
  }

  function trackLead(email) {
    var eventId = newEventId();
    var clean = (email || '').trim().toLowerCase();
    ttqTrack('SubmitForm', { currency: CURRENCY }, eventId);
    safe(function () {
      if (clean && window.ttq && typeof window.ttq.identify === 'function') window.ttq.identify({ email: clean });
    });
    var payload = { event: 'generate_lead', event_id: eventId, currency: CURRENCY };
    if (clean) payload.user_data = { email_address: clean };
    dlPush(payload);
    gtagEvent('generate_lead', { currency: CURRENCY });
    safe(function () {
      if (clean && window.omnisend) window.omnisend.push(['identifyContact', { email: clean }]);
    });
    debugLog('Lead', { email: clean }, eventId);
    return eventId;
  }

  function trackSearch(query) {
    var eventId = newEventId();
    var q = (query || '').trim();
    ttqTrack('Search', { query: q, currency: CURRENCY }, eventId);
    dlPush({ event: 'search', event_id: eventId, search_term: q });
    gtagEvent('search', { search_term: q });
    debugLog('Search', { search_string: q }, eventId);
    return eventId;
  }

  window.TBTrack = {
    captureAttribution: captureAttribution,
    getAttribution: getAttribution,
    trackViewItem: trackViewItem,
    trackAddToCart: trackAddToCart,
    trackAddToCartFromAddResponse: trackAddToCartFromAddResponse,
    trackBeginCheckout: trackBeginCheckout,
    trackBeginCheckoutFromShopifyCart: trackBeginCheckoutFromShopifyCart,
    trackLead: trackLead,
    trackSearch: trackSearch
  };

  document.addEventListener('DOMContentLoaded', function () {
    safe(function () {
      if (window.omnisend) window.omnisend.push(['track', '$pageViewed']);
    });
    captureAttribution();
  });
})(window, document);
